import { getRouteApi } from "@tanstack/react-router";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  ArrowRight,
  Phone,
  Check,
  Sparkles,
  Target,
  Zap,
  BadgeCheck,
  ChevronRight,
} from "lucide-react";
import { LeadFormModal } from "@/components/LeadFormModal";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { speakableSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import {
  getIndustryBySlug,
  INDUSTRY_SLUGS,
  type IndustrySolution,
} from "@/data/industry-solutions";
import { SERVICES } from "@/data/services";

const routeApi = getRouteApi("/industries/$slug/");

export function IndustryDetailPage() {
  const { ind } = routeApi.useLoaderData() as { ind: IndustrySolution };

  return (
    <main className="min-h-screen bg-navy-deep text-white">
      <nav
        aria-label="Breadcrumb"
        className="border-b border-white/10 bg-[#0a1a3d]/80 px-4 py-3 text-xs text-white/80"
      >
        <div className="mx-auto flex max-w-6xl items-center gap-1.5">
          <Link to="/" className="hover:text-amber-300">
            หน้าแรก
          </Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/industries" className="hover:text-amber-300">
            Industry Hub
          </Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-white/85">{ind.name}</span>
        </div>
      </nav>

      <section className="relative overflow-hidden border-b border-white/10 bg-gradient-to-br from-[#0a1a3d] via-[#0d2150] to-[#0a1a3d] px-4 py-14 sm:py-20">
        <div
          className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(251,191,36,0.12),transparent_60%)]"
          aria-hidden
        />
        <div className="relative mx-auto max-w-6xl">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-200">
            <Sparkles className="h-3.5 w-3.5" /> Industry Solution · B2B Pack
          </span>
          <h1 className="aeo-speakable mt-4 max-w-3xl text-3xl font-extrabold leading-tight sm:text-5xl">
            <span className="bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent">
              {ind.name}
            </span>
          </h1>
          <p className="mt-2 text-sm font-semibold uppercase tracking-wider text-amber-300/90">
            {ind.tagline}
          </p>
          <p className="aeo-speakable mt-5 max-w-3xl text-base text-white/85 sm:text-lg">
            {ind.heroIntro}
          </p>
          <p className="mt-3 text-sm text-white/80">เหมาะกับ: {ind.audience}</p>

          <div className="mt-7 flex flex-wrap gap-3">
            <LeadFormModal
              trigger={
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-navy-deep shadow-xl shadow-amber-500/30 hover:bg-amber-400"
                >
                  ขอใบเสนอราคา Industry Pack <ArrowRight className="h-4 w-4" />
                </button>
              }
            />
            <a
              href="tel:+66832494999"
              className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white hover:bg-white/10"
            >
              <Phone className="h-4 w-4" /> ปรึกษาฟรี 083-249-4999
            </a>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-3 sm:gap-6">
            {ind.metrics.map((m) => (
              <div
                key={m.label}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-center sm:p-5"
              >
                <div className="text-2xl font-extrabold text-amber-300 sm:text-3xl">{m.value}</div>
                <div className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-white/80 sm:text-xs">
                  {m.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 px-4 py-14 sm:py-16">
        <div className="mx-auto max-w-6xl">
          <h2 className="aeo-speakable text-2xl font-extrabold sm:text-3xl">
            ปัญหาในอุตสาหกรรม → โซลูชันของเรา
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-white/70">
            เราออกแบบ workflow เฉพาะ {ind.name} จากข้อมูลกว่า 100+ เคสในกลุ่มเดียวกัน
          </p>
          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {ind.painPoints.map((p, i) => {
              const sol = ind.solutions[i];
              return (
                <div
                  key={p.title}
                  className="rounded-2xl border border-white/10 bg-gradient-to-br from-rose-500/[0.06] to-white/[0.02] p-5 sm:p-6"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-rose-500/15 text-rose-300">
                      <Target className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-rose-300">
                        Pain Point
                      </p>
                      <h3 className="mt-0.5 text-base font-bold leading-snug">{p.title}</h3>
                      <p className="mt-1.5 text-sm text-white/80">{p.desc}</p>
                    </div>
                  </div>
                  {sol && (
                    <div className="mt-4 rounded-xl border border-amber-400/20 bg-amber-500/[0.08] p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-500/20 text-amber-200">
                          <Zap className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-[10px] font-bold uppercase tracking-widest text-amber-200">
                            NYC Solution
                          </p>
                          <h4 className="mt-0.5 text-sm font-bold text-amber-50">{sol.title}</h4>
                          <p className="mt-1 text-xs text-white/70">{sol.desc}</p>
                          {sol.serviceId && SERVICES[sol.serviceId] && (
                            <Link
                              to="/services/$service"
                              params={{ service: sol.serviceId }}
                              className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-amber-300 hover:text-amber-200"
                            >
                              ดูบริการ {SERVICES[sol.serviceId].nameTh}{" "}
                              <ArrowRight className="h-3 w-3" />
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 bg-[#0d2150] px-4 py-14 sm:py-16">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">เอกสารและงานส่งมอบหลัก</h2>
          <p className="mt-2 max-w-2xl text-sm text-white/70">
            รวมทุกสิ่งที่ {ind.audience} ต้องใช้บ่อยที่สุด
          </p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {ind.deliverables.map((d) => (
              <div
                key={d}
                className="flex items-start gap-2.5 rounded-xl border border-white/10 bg-white/[0.04] p-4"
              >
                <Check className="mt-0.5 h-4 w-4 flex-none text-amber-300" />
                <span className="text-sm text-white/85">{d}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 px-4 py-14 sm:py-16">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">กระบวนการทำงาน</h2>
          <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {ind.process.map((s, i) => (
              <div
                key={s.name}
                className="relative rounded-2xl border border-white/10 bg-white/[0.04] p-5"
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-500/20 text-sm font-bold text-amber-300">
                  {i + 1}
                </div>
                <h3 className="mt-3 text-base font-bold">{s.name}</h3>
                <p className="mt-1.5 text-xs text-white/80">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 bg-gradient-to-b from-[#0a1a3d] to-[#0d2150] px-4 py-14 sm:py-16">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">แพ็กเกจสำหรับ {ind.name}</h2>
          <p className="mt-2 text-sm text-white/70">
            ปรับขนาดได้ตามทีมและปริมาณงาน — ติดต่อขอ custom proposal ได้ทุก tier
          </p>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {ind.packages.map((p) => (
              <div
                key={p.name}
                className={`relative rounded-2xl border p-6 ${
                  p.featured
                    ? "border-amber-400/60 bg-gradient-to-br from-amber-500/[0.15] to-amber-500/[0.04] shadow-2xl shadow-amber-500/10 ring-1 ring-amber-400/30"
                    : "border-white/10 bg-white/[0.04]"
                }`}
              >
                {p.featured && (
                  <span className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-amber-500 px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider text-navy-deep">
                    <BadgeCheck className="h-3 w-3" /> Most Popular
                  </span>
                )}
                <h3 className="text-lg font-extrabold">{p.name}</h3>
                <p className="mt-2">
                  <span className="text-3xl font-extrabold text-amber-300">{p.price}</span>
                  <span className="ml-1 text-xs text-white/80">{p.per}</span>
                </p>
                <ul className="mt-4 space-y-2">
                  {p.includes.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm text-white/80">
                      <Check className="mt-0.5 h-4 w-4 flex-none text-amber-300" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
                <LeadFormModal
                  trigger={
                    <button
                      type="button"
                      className={`mt-5 w-full rounded-xl px-4 py-2.5 text-sm font-bold transition ${
                        p.featured
                          ? "bg-amber-500 text-navy-deep hover:bg-amber-400"
                          : "border border-white/20 bg-white/5 text-white hover:bg-white/10"
                      }`}
                    >
                      ขอใบเสนอราคา · {p.name}
                    </button>
                  }
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 px-4 py-14 sm:py-16">
        <div className="mx-auto max-w-4xl rounded-3xl border border-amber-400/20 bg-gradient-to-br from-amber-500/[0.08] to-white/[0.02] p-8 sm:p-12">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-300">
            Case Study
          </div>
          <h2 className="mt-2 text-2xl font-extrabold sm:text-3xl">{ind.caseStudy.client}</h2>
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-rose-300">
                Challenge
              </p>
              <p className="mt-1.5 text-sm text-white/80">{ind.caseStudy.challenge}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-300">
                Result
              </p>
              <p className="mt-1.5 text-sm text-white/80">{ind.caseStudy.result}</p>
            </div>
          </div>
          <div className="mt-6 rounded-xl border border-amber-400/30 bg-amber-500/[0.08] p-4 text-center">
            <p className="text-[10px] font-bold uppercase tracking-widest text-amber-300">Impact</p>
            <p className="mt-1 text-lg font-extrabold text-amber-100">{ind.caseStudy.metric}</p>
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 bg-[#0d2150] px-4 py-14">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-xl font-extrabold sm:text-2xl">บริการที่เชื่อมโยงกับ {ind.name}</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {ind.relatedServiceIds.map((id) => {
              const s = SERVICES[id];
              if (!s) return null;
              return (
                <Link
                  key={id}
                  to="/industries/$slug/$service"
                  params={{ slug: ind.slug, service: id }}
                  className="group rounded-xl border border-white/10 bg-white/[0.04] p-4 transition hover:border-amber-400/50 hover:bg-white/[0.07]"
                >
                  <div className="text-sm font-bold text-white group-hover:text-amber-200">
                    {s.nameTh}
                  </div>
                  <div className="mt-1 text-[11px] text-white/80">
                    ฿{s.priceFrom.toLocaleString()} · {s.turnaround}
                  </div>
                  <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-amber-300">
                    เปิด Pack สำหรับ {ind.name} <ArrowRight className="h-3 w-3" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 px-4 py-14 sm:py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="aeo-speakable text-2xl font-extrabold sm:text-3xl">
            คำถามที่พบบ่อย — {ind.name}
          </h2>
          <div className="mt-6 space-y-3">
            {ind.faqs.map((f, i) => (
              <details
                key={i}
                className="group rounded-2xl border border-white/10 bg-white/[0.04] p-5 open:border-amber-400/30 open:bg-white/[0.06]"
              >
                <summary className="cursor-pointer list-none text-sm font-bold text-white marker:hidden">
                  <span className="mr-2 text-amber-300">Q.</span>
                  {f.q}
                </summary>
                <p className="aeo-speakable mt-3 text-sm leading-relaxed text-white/75">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 bg-[#0a1a3d] px-4 py-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-lg font-extrabold sm:text-xl">สำรวจอุตสาหกรรมอื่นๆ</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {INDUSTRY_SLUGS.filter((s) => s !== ind.slug).map((s) => {
              const other = getIndustryBySlug(s)!;
              return (
                <Link
                  key={s}
                  to="/industries/$slug"
                  params={{ slug: s }}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs font-semibold text-white/80 hover:border-amber-400/40 hover:bg-amber-500/10 hover:text-amber-200"
                >
                  {other.name}
                </Link>
              );
            })}
            <Link
              to="/industries"
              className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/30 bg-amber-500/10 px-3 py-1.5 text-xs font-bold text-amber-200 hover:bg-amber-500/15"
            >
              ดู Industry Hub ทั้งหมด <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 py-14">
        <div className="mx-auto max-w-3xl rounded-3xl border border-amber-400/30 bg-gradient-to-br from-amber-500/10 to-amber-500/5 p-8 text-center sm:p-12">
          <h2 className="text-2xl font-extrabold sm:text-3xl">
            ต้องการ Custom Pack สำหรับ {ind.name}?
          </h2>
          <p className="mt-3 text-white/75">
            Dedicated Account Manager + SLA 24 ชม. + ราคา Retainer ลดสูงสุด 25%
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <LeadFormModal
              trigger={
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-navy-deep hover:bg-amber-400"
                >
                  ขอใบเสนอราคา · ฟรี
                </button>
              }
            />
            <a
              href="tel:+66832494999"
              className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white hover:bg-white/10"
            >
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
